/**
 * HTTP Client for API calls
 * Base URL: Configured via REACT_APP_API_URL environment variable
 *
 * Response Format:
 * {
 *   "success": true,
 *   "data": { } or [],
 *   "message": "MESSAGE",
 *   "timestamp": "2025-11-09T09:06:09+01:00"
 * }
 *
 * Supports encryption/decryption based on environment configuration
 */

import { encrypt, decrypt, shouldEncrypt, getCommunicationKey } from './encryption';
import { getSecureItem, setSecureItem, removeSecureItem } from './secureStorage';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Get the authentication token
 * @returns {Promise<string|null>} The authentication token or null
 */
const getAuthToken = async () => {
  try {
    // Try to get from secure storage first (new method)
    const token = await getSecureItem('authToken');
    if (token) return token;

    // Fallback to regular localStorage for backward compatibility
    return localStorage.getItem('authToken');
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

/**
 * Set the authentication token
 * @param {string} token - The token to set
 */
const setAuthToken = async (token) => {
  if (token) {
    try {
      await setSecureItem('authToken', token);
      // Keep in regular localStorage for now (backward compatibility)
      localStorage.setItem('authToken', token);
    } catch (error) {
      console.error('Error setting auth token:', error);
      // Fallback to regular localStorage
      localStorage.setItem('authToken', token);
    }
  } else {
    try {
      await removeSecureItem('authToken');
      localStorage.removeItem('authToken');
    } catch (error) {
      console.error('Error removing auth token:', error);
      localStorage.removeItem('authToken');
    }
  }
};

class HttpClient {
  constructor(baseURL = BASE_URL) {
    this.baseURL = baseURL;
    this.defaultHeaders = {
      'Content-Type': 'application/json'
    };
  } /**
   * Build headers with authorization and communication key
   */
  async buildHeaders(customHeaders = {}) {
    const headers = { ...this.defaultHeaders, ...customHeaders };
    const token = await getAuthToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add communication key if available (bypasses encryption)
    const communicationKey = getCommunicationKey();
    if (communicationKey) {
      headers['X-Communication-Key'] = communicationKey;
    }

    return headers;
  }

  /**
   * Handle API response with decryption support
   */
  async handleResponse(response) {
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');
    const data = isJson ? await response.json() : await response.text();

    let responseData = data;
    if (data.encrypted === true && data.data) {
      try {
        const decryptedData = await decrypt(data.data);
        // Ensure decrypted data is parsed as JSON if it's a string
        responseData = typeof decryptedData === 'string' ? JSON.parse(decryptedData) : decryptedData;
      } catch (error) {
        throw new Error('Failed to decrypt response data');
      }
    }

    if (!response.ok) {
      // Handle HTTP errors with decrypted data
      let errorMessage = null;

      // Try to extract actual error message from response
      if (responseData.error && typeof responseData.error === 'object' && responseData.error.message) {
        errorMessage = responseData.error.message;
      } else if (responseData.error && typeof responseData.error === 'string') {
        errorMessage = responseData.error;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      }

      // If no specific error message found, use appropriate default
      if (!errorMessage) {
        // Use friendly message only for 5xx errors (server issues)
        if (response.status >= 500) {
          errorMessage = `Oops! We're taking a quick breather... Try again in a moment! 🥤`;
        } else {
          errorMessage = `Something went wrong with your request. Please try again.`;
        }
      }

      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = responseData;
      throw error;
    }

    // Check API success flag
    if (responseData.success === false) {
      let errorMessage = 'API request failed';
      if (responseData.error && typeof responseData.error === 'object' && responseData.error.message) {
        errorMessage = responseData.error.message;
      } else if (responseData.error && typeof responseData.error === 'string') {
        errorMessage = responseData.error;
      } else if (responseData.message) {
        errorMessage = responseData.message;
      }

      const error = new Error(errorMessage);
      error.data = responseData;
      throw error;
    }

    return responseData;
  }

  /**
   * Handle API errors with token refresh on 401
   */
  async handleError(error, endpoint, options) {
    if (error.status === 401 && !endpoint.includes('/auth/refresh')) {
      // Try to refresh token
      try {
        const refreshToken = await getSecureItem('refreshToken');
        if (refreshToken) {
          // Attempt token refresh
          const refreshUrl = `${this.baseURL}/auth/refresh`;
          const refreshResponse = await fetch(refreshUrl, {
            method: 'POST',
            headers: this.defaultHeaders,
            body: JSON.stringify({ refresh_token: refreshToken })
          });

          if (refreshResponse.ok) {
            const data = await refreshResponse.json();
            if (data.data?.tokens?.access_token) {
              await setAuthToken(data.data.tokens.access_token);
              if (data.data.tokens.refresh_token) {
                await setSecureItem('refreshToken', data.data.tokens.refresh_token);
              }

              // Retry the original request
              return await this.request(endpoint, options);
            }
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }

      // If refresh failed, clear tokens and redirect
      await setAuthToken(null);
      await removeSecureItem('refreshToken');
      await removeSecureItem('user');
      // window.location.href = '/auth';
    }

    throw error;
  }

  /**
   * Generic request method
   */
  async request(endpoint, options = {}) {
    try {
      const url = `${this.baseURL}${endpoint}`;
      const response = await fetch(url, options);
      return await this.handleResponse(response);
    } catch (error) {
      return this.handleError(error, endpoint, options);
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}, customHeaders = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = `${endpoint}${queryString ? `?${queryString}` : ''}`;

    return await this.request(url, {
      method: 'GET',
      headers: await this.buildHeaders(customHeaders)
    });
  }

  /**
   * POST request with encryption support
   */
  async post(endpoint, data = {}, customHeaders = {}) {
    // Encrypt data if encryption is enabled
    let body;
    if (shouldEncrypt()) {
      const encryptedData = await encrypt(data);
      body = JSON.stringify({ encryptedData });
    } else {
      body = JSON.stringify(data);
    }

    return await this.request(endpoint, {
      method: 'POST',
      headers: await this.buildHeaders(customHeaders),
      body: body
    });
  }

  /**
   * PUT request with encryption support
   */
  async put(endpoint, data = {}, customHeaders = {}) {
    // Encrypt data if encryption is enabled
    let body;
    if (shouldEncrypt()) {
      const encryptedData = await encrypt(data);
      body = JSON.stringify({ encryptedData });
    } else {
      body = JSON.stringify(data);
    }

    return await this.request(endpoint, {
      method: 'PUT',
      headers: await this.buildHeaders(customHeaders),
      body: body
    });
  }

  /**
   * PATCH request with encryption support
   */
  async patch(endpoint, data = {}, customHeaders = {}) {
    // Encrypt data if encryption is enabled
    let body;
    if (shouldEncrypt()) {
      const encryptedData = await encrypt(data);
      body = JSON.stringify({ encryptedData });
    } else {
      body = JSON.stringify(data);
    }

    return await this.request(endpoint, {
      method: 'PATCH',
      headers: await this.buildHeaders(customHeaders),
      body: body
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, customHeaders = {}) {
    return await this.request(endpoint, {
      method: 'DELETE',
      headers: await this.buildHeaders(customHeaders)
    });
  }

  /**
   * Upload file with FormData
   */
  async upload(endpoint, formData, customHeaders = {}) {
    // Don't set Content-Type for FormData - browser will set it with boundary
    const headers = { ...customHeaders };
    const token = await getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Add communication key if available
    const communicationKey = getCommunicationKey();
    if (communicationKey) {
      headers['X-Communication-Key'] = communicationKey;
    }

    return await this.request(endpoint, {
      method: 'POST',
      headers,
      body: formData
    });
  }
}

// Create and export singleton instance
const httpClient = new HttpClient();

// Export the token management functions
export { getAuthToken, setAuthToken };

export default httpClient;
